import { UI } from "@wwimmo/ui";
import { ChangeEvent, useCallback, useContext, useEffect, useState } from "react";
import styles from "./FileSelector.module.css";
import { v4 as uuidv4 } from "uuid";
import Resizer from "react-image-file-resizer";
import { useTranslation } from "react-i18next";
import { ColorStyle } from "src/utils/Colors";
import { observer } from "mobx-react";
import * as Sentry from "@sentry/react";
import { FileSelectorItemModal } from "./modal/FileSelectorItemModal";
import { useThumbnailRetry } from "src/hooks/thumbnail/useThumbnailRetry";
import { IFile } from "src/types";
import { FILE_STATE, FILE_TYPE } from "src/enums";
import { formatDateForFile } from "src/stores/krediflow/Utils";
import { NetworkConfig } from "src/network/NetworkConfig";
import { RootStoreContext } from "src/stores/RootStore";
import { MessageType } from "src/components/notifications/Notifier";

export enum FILE_PREVIEW_MODE {
    BROWSER = "browser",
    MODAL = "modal"
}

enum FILE_PREVIEW_TYPE {
    GENERAL = "general",
    PDF = "pdf"
}

interface FileSelectorItemProps {
    label: string;
    file?: IFile;
    onSelectNewFilePropFunc?: (newValue: IFile) => void;
    onDeleteFilePropFunc?: (fileId: string) => Promise<void>;
    fileType: FILE_TYPE;
    disabled?: boolean;
    resizeImage?: boolean;
    filePreviewMode?: FILE_PREVIEW_MODE;
    canDelete?: boolean;
    fileSelectorItemStyles?: React.CSSProperties;
    allowAllFileTypes?: boolean;
}
interface FileHTMLInputEvent extends ChangeEvent {
    target: HTMLInputElement & EventTarget;
}
const dataTypes = ["image/jpeg", "image/png", "application/pdf"];
const imagesDataTypes = ["image/jpeg", "image/png"];
const extensions = [".jpg", ".jpeg", ".png", ".pdf"];

const MAX_IMAGE_DIMENSION = {
    WIDTH: 640,
    HEIGHT: 640
};

const FileSelectorItemBase = (props: FileSelectorItemProps) => {
    const { t } = useTranslation();
    const { uiStore } = useContext(RootStoreContext);

    const {
        label,
        file,
        onSelectNewFilePropFunc,
        onDeleteFilePropFunc,
        fileType,
        disabled,
        resizeImage,
        filePreviewMode = FILE_PREVIEW_MODE.BROWSER,
        canDelete = true,
        allowAllFileTypes = false
    } = props;

    const isFileTypeImage = file?.mimetype && imagesDataTypes.includes(file.mimetype);
    const isFileTypePdf = file?.mimetype && file.mimetype === "application/pdf";

    // If the file has a URL it means that it is new file (selected locally, not saved to db)
    // If the file has a fileId it means that it is an existing file (loaded from db). If it is an image, we load the thumbnail for the preview if its a pdf provide the link to load it directly
    const fileUrl = file?.url
        ? file.url
        : file?.fileId
          ? isFileTypeImage
              ? NetworkConfig.openThumbnailUrl + file.fileId
              : NetworkConfig.datafileUrl + file.fileId
          : undefined;

    const { src: previewSrc, retryOnError } = useThumbnailRetry(fileUrl);

    // In this case we are not displaying the file preview and disable the onClick function
    const noFileProvided = file === undefined;

    const [isImagePreviewLoading, setIsImagePreviewLoading] = useState<boolean>(isFileTypeImage ? true : false);
    const [isFileDeleting, setIsFileDeleting] = useState<boolean>(false);
    const [hadErrorLoadingImage, setHadErrorLoadingImage] = useState<boolean>(false);
    const [isFileModalDisplayed, setIsFileModalDisplayed] = useState<boolean>(false);

    useEffect(() => {
        return () => {
            if (file?.url) {
                URL.revokeObjectURL(file.url);
            }
        };
    }, [file?.url]);

    const resizeImageFile = useCallback((file: File): Promise<string> => {
        return new Promise(async (resolve) => {
            const mimeTypeParts = file.type.split("/");

            Resizer.imageFileResizer(
                file,
                MAX_IMAGE_DIMENSION.WIDTH,
                MAX_IMAGE_DIMENSION.HEIGHT,
                mimeTypeParts.length > 1 ? mimeTypeParts[1] : "jpeg",
                100, // image quality
                0, // rotation
                (uri) => {
                    if (typeof uri === "string") {
                        resolve(uri);
                    } else {
                        resolve("");
                    }
                },
                "base64"
            );
        });
    }, []);

    const onSelectNewFile = useCallback(
        async (event: FileHTMLInputEvent) => {
            if (event?.target?.files) {
                const inputFilesList = Array.from(event.target.files);

                if (inputFilesList.length > 0) {
                    const inputFile: File = inputFilesList[0];
                    const inputFileUrl = URL.createObjectURL(inputFile);

                    const newFile: IFile = { fileBlob: inputFile } as IFile;

                    newFile.fileId = uuidv4();
                    newFile.entityFileId = uuidv4();
                    newFile.name = inputFile.name;
                    newFile.url = inputFileUrl;
                    newFile.type = fileType;
                    newFile.mimetype = inputFile.type;
                    newFile.extension = inputFile.name.split(".").pop();
                    newFile.fileDate = formatDateForFile(new Date(inputFile.lastModified));
                    newFile.state = FILE_STATE.NEW;

                    let fileBase64String: string;

                    const isOfAllowedFileType = allowAllFileTypes || dataTypes.includes(inputFile.type);

                    if (isOfAllowedFileType) {
                        const isImageFile = imagesDataTypes.includes(inputFile.type);

                        if (resizeImage && isImageFile) {
                            const resizedImageDataURL = await resizeImageFile(inputFile);

                            if (resizedImageDataURL) {
                                fileBase64String = resizedImageDataURL.split(",")[1];
                            } else {
                                console.error("Error resizing image");
                                uiStore.printStatusMessage(
                                    `${t("components.file_selector.error.image_resizing_error")}`,
                                    MessageType.ERROR
                                );
                                return; // Exit early if resizing fails
                            }
                        } else {
                            // Use FileReader to get base64 string for non-resized files
                            try {
                                fileBase64String = await new Promise<string>((resolve, reject) => {
                                    const reader = new FileReader();
                                    reader.onload = () => {
                                        const result = reader.result as string;
                                        const base64 = result.split(",")[1];
                                        if (base64) {
                                            resolve(base64);
                                        } else {
                                            reject(new Error("Failed to extract base64 string"));
                                        }
                                    };
                                    reader.onerror = () => reject(new Error("Error reading file"));
                                    reader.readAsDataURL(inputFile);
                                });
                            } catch (error) {
                                console.error("Error reading file as base64:", error);
                                uiStore.printStatusMessage(
                                    `${t("components.file_selector.error.image_reading_error")}`,
                                    MessageType.ERROR
                                );
                                return;
                            }
                        }
                        newFile.base64String = fileBase64String;
                    } else {
                        uiStore.printStatusMessage(
                            `${t("components.file_selector.error.file_type_error")}`,
                            MessageType.ERROR
                        );
                        return;
                    }

                    if (onSelectNewFilePropFunc) {
                        onSelectNewFilePropFunc(newFile);
                    }

                    // Reset the file input to allow re-selecting the same file
                    event.target.value = "";
                }
            }
        },
        [resizeImageFile, fileType, onSelectNewFilePropFunc, resizeImage, uiStore, t, allowAllFileTypes]
    );

    const onDeleteFile = useCallback(async () => {
        setIsFileDeleting(true);

        if (file && file.fileId) {
            if (onDeleteFilePropFunc) {
                await onDeleteFilePropFunc(file.fileId);
            }
        }

        setIsFileDeleting(false);
    }, [file, onDeleteFilePropFunc]);

    const onImageFileLoad = useCallback(() => {
        setIsImagePreviewLoading(false);
    }, []);

    const openFilePreview = useCallback(
        (checkForIconClick: boolean) => (e: any) => {
            let hasClickedOnIcon = false;

            if (checkForIconClick) {
                hasClickedOnIcon = e.target.localName === "path";
            }

            if (!hasClickedOnIcon) {
                if (filePreviewMode === FILE_PREVIEW_MODE.MODAL) {
                    setIsFileModalDisplayed(true);
                } else if (filePreviewMode === FILE_PREVIEW_MODE.BROWSER) {
                    const url = file?.url ?? NetworkConfig.datafileUrl + file?.fileId;
                    const newWindow = window.open(url, "_blank", "noopener,noreferrer");
                    if (newWindow) {
                        newWindow.opener = null; // Prevents the new page from being able to access the window.opener property
                    }
                }
            }
        },
        [filePreviewMode, file?.fileId, file?.url]
    );

    const onImgeError = useCallback(() => {
        if (retryOnError()) {
            return;
        }

        setHadErrorLoadingImage(true);
        setIsImagePreviewLoading(false);
    }, [retryOnError]);

    const imagePreviewElement = (
        <img src={previewSrc} alt="" onLoad={onImageFileLoad} onError={onImgeError} style={{ display: "none" }} />
    );

    const FilePreviewElement = ({ type }: { type: FILE_PREVIEW_TYPE }) => (
        <div className={styles.PdfPreviewContainer}>
            <UI.Icon
                style={{ position: "absolute", top: 40, right: 52 }}
                color="var(--grey)"
                icon={type === FILE_PREVIEW_TYPE.PDF ? UI.SVGIcon.Pdf : UI.SVGIcon.Documents}
                className="file-selector-item-pdf-icon"
                onClick={noFileProvided ? undefined : openFilePreview(false)}
                size={40}
            />
            <span className="truncate-text">{file?.name}</span>
        </div>
    );

    const toggleDisplayFileModal = useCallback(() => {
        setIsFileModalDisplayed((isDisplayed) => !isDisplayed);
    }, []);

    return (
        <>
            <FileSelectorItemModal
                file={file}
                isDisplayed={isFileModalDisplayed}
                toggleDisplayFileModal={toggleDisplayFileModal}
            />
            <li
                className={`${styles.FileSelectorItem} ${
                    fileType === FILE_TYPE.INVOICE_ATTACHMENT ? styles.KrediFlowAttachment : ""
                } ${disabled && !fileUrl ? styles.FileSelectorItemDisabled : ""} ${
                    fileUrl && !hadErrorLoadingImage ? styles.EnlargeFileCursor : ""
                }`}
                style={{
                    backgroundImage: previewSrc && !isFileDeleting ? `url(${previewSrc})` : "none",
                    ...props.fileSelectorItemStyles
                }}
                onClick={noFileProvided ? undefined : openFilePreview(true)}
            >
                {(isImagePreviewLoading && fileUrl) || isFileDeleting ? (
                    <UI.RotatingSpinner noLogo={true} size={25} />
                ) : undefined}
                {isFileTypeImage ? imagePreviewElement : <></>}
                {isFileTypePdf ? <FilePreviewElement type={FILE_PREVIEW_TYPE.PDF} /> : <></>}
                {!isFileTypeImage && !isFileTypePdf && file ? (
                    <FilePreviewElement type={FILE_PREVIEW_TYPE.GENERAL} />
                ) : (
                    <></>
                )}
                {hadErrorLoadingImage ? (
                    <div className={styles.FileLoadingErrorContainer}>
                        <UI.Icon
                            icon={UI.SVGIcon.CloseCircle}
                            color={ColorStyle("danger")}
                            size={40}
                            className="mb-1"
                        />
                        <span>{t("screens.tickets.form.notification.image_loading_error")}</span>
                    </div>
                ) : undefined}
                {!fileUrl ? (
                    <>
                        <span
                            className={`${styles.FileSelectorItemLabel} ${
                                fileType === FILE_TYPE.INVOICE_ATTACHMENT ? styles.KrediFlowAttachment : ""
                            }`}
                        >
                            {label}
                        </span>
                        <UI.Icon
                            className={`${styles.InvoiceAttachmentIcon} ${
                                fileType === FILE_TYPE.INVOICE_ATTACHMENT ? styles.KrediFlowAttachment : ""
                            }`}
                            color="var(--grey)"
                            icon={UI.SVGIcon.PaperclipAdd}
                        />
                        <input
                            className={styles.FileSelectorInput}
                            type="file"
                            onChange={onSelectNewFile}
                            accept={allowAllFileTypes ? "*" : dataTypes.concat(extensions).join(",")}
                        />
                    </>
                ) : (
                    canDelete && (
                        <div className={`${styles.FileSelectorItemDelete} ${disabled ? styles.Disabled : ""}`}>
                            <UI.Icon onClick={onDeleteFile} color="var(--grey)" icon={UI.SVGIcon.Trash} />
                        </div>
                    )
                )}
            </li>
        </>
    );
};

export const FileSelectorItem = Sentry.withProfiler(observer(FileSelectorItemBase));
